import axios from 'axios';
import { getBusinessIdentifier, getProjectId } from '../utils/auth';
import type { AuthResponse } from '../types/analytics';
import { clearRequestCache } from './requestCache';

// Re-use API_URL from api.ts or define locally if needed
// Assuming API_URL is accessible, otherwise re-define:
const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_ANALYTICS_SERVICE_URL || 'http://localhost:8000';
const BASE_URL = "https://api.floorselector.convrse.ai";
// const BASE_URL = "http://localhost:8000/api";

class AuthService {
    // Store keys as constants
    private readonly TOKEN_KEY = 'token';
    private readonly USER = 'user';
    private readonly TOKEN_TYPE_KEY = 'token_type';
    private readonly BUSINESS_IDENTIFIER_KEY = 'business_identifier';

    async login(email: string, password: string): Promise<AuthResponse> {
        const formData = new URLSearchParams();
        formData.append('username', email);
        formData.append('password', password);

        // We still fetch these to potentially *send* them with the login request,
        // depending on backend requirements, but we won't use this fetched
        // businessIdentifier as the value to store upon success.
        const businessIdentifierFromUtil = getBusinessIdentifier();
        // **Always append business_identifier, even if empty, as backend seems to require it.**
        formData.append('business_identifier', businessIdentifierFromUtil);

        const projectId = getProjectId(); // getProjectId might return string | null
        if (projectId) {
            formData.append('project_id', projectId);
        }

        try {
            // Use a separate axios instance for login if it shouldn't use interceptors
            const response = await axios.post<AuthResponse>(`${API_URL}/login`, formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
            
            // Check for token AND business identifier in the response
            if (response.data.access_token) {
                localStorage.setItem(this.TOKEN_KEY, response.data.access_token);
                localStorage.setItem(this.USER, JSON.stringify(response.data.user));
                localStorage.setItem(this.TOKEN_TYPE_KEY, response.data.token_type || 'Bearer');

                // **Store the identifier returned by the API**
                // Ensure your AuthResponse type includes 'business_identifier: string;'
                if (response.data.business_identifier) {
                    localStorage.setItem(this.BUSINESS_IDENTIFIER_KEY, response.data.business_identifier);
                } else {
                    // Optional: Handle cases where the API *should* return it but didn't
                    console.warn('[authService] Login successful, but business_identifier missing in API response.');
                    // Fallback: uncomment below to store the pre-fetched one if that's desired behavior
                    // if (businessIdentifierFromUtil) {
                    //    localStorage.setItem(this.BUSINESS_IDENTIFIER_KEY, businessIdentifierFromUtil);
                    // }
                }
            }
            return response.data;
        } catch (error: any) {
            // Humanize error messages
            let errorMessage = 'Login failed';
            
            // Check for network errors
            if (!error.response) {
                if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
                    errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
                } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                    errorMessage = 'The request took too long. Please check your internet connection and try again.';
                } else if (error.code === 'EHOSTUNREACH' || error.message?.includes('EHOSTUNREACH')) {
                    errorMessage = 'Unable to reach the server. Please check your internet connection and try again.';
                } else if (error.code === 'ECONNREFUSED' || error.message?.includes('ECONNREFUSED')) {
                    errorMessage = 'Connection refused. The server may be temporarily unavailable. Please try again later.';
                } else {
                    errorMessage = 'Network error occurred. Please check your internet connection and try again.';
                }
            } else {
                // Extract error message from API response
                const rawMessage = error.response?.data?.error || 
                                  error.response?.data?.message || 
                                  error.response?.data?.detail || 
                                  error.message || 
                                  'Login failed';
                
                // Check for technical error patterns in the message
                const errorString = String(rawMessage).toLowerCase();
                if (errorString.includes('ehostunreach') || errorString.includes('connect ehostunreach')) {
                    errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
                } else {
                    errorMessage = rawMessage;
                }
            }
            
            throw new Error(errorMessage);
        }
    }

    logout(): void {
        // Preserve business_identifier before clearing other data
        const businessIdentifier = localStorage.getItem(this.BUSINESS_IDENTIFIER_KEY);

        // Drop any cached API responses so the next user can't see this
        // session's data from memory.
        clearRequestCache();

        // Clear authentication tokens
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.TOKEN_TYPE_KEY);
        localStorage.removeItem(this.USER);
        localStorage.removeItem('meetingPaginationState');
        
        // Clear all chat messages and session IDs from localStorage
        try {
            // Clear chat data for current business identifier if available
            if (businessIdentifier) {
                localStorage.removeItem(`chat_messages_${businessIdentifier}`);
                localStorage.removeItem(`chat_session_${businessIdentifier}`);
            }
            
            // Also clear all chat-related keys (in case there are multiple business identifiers)
            const keysToRemove: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.startsWith('chat_messages_') || key.startsWith('chat_session_'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (error) {
            console.error("Error clearing chat data from localStorage:", error);
        }
        
        // Restore business_identifier if it existed
        if (businessIdentifier) {
            localStorage.setItem(this.BUSINESS_IDENTIFIER_KEY, businessIdentifier);
        } else {
            localStorage.removeItem(this.BUSINESS_IDENTIFIER_KEY);
        }
        
    }

    async logoutAllSalesPerson(): Promise<void> {
        const businessIdentifier = localStorage.getItem(this.BUSINESS_IDENTIFIER_KEY);
        try {
        
        if (!businessIdentifier) {
            return;
        }

        const formData = new URLSearchParams();
        formData.append('business_id', businessIdentifier);


        await axios.post(`${BASE_URL}/api/sales-person/logout-allsalesperson`, formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });

        // Clear localStorage after successful API call
        // this.clearLocalStorage();

    } catch (error: any) {
        console.error('[authService] Logout Error:', error);
        // Clear localStorage even if API call fails to ensure client-side cleanup
        // this.clearLocalStorage();
        const message = error.response?.data?.detail || error.message || 'Logout failed';
        throw new Error(message);
    }
}
        async forceLogoutSalesPerson(email: string, user_id: any): Promise<void> {
            const businessIdentifier = localStorage.getItem(this.BUSINESS_IDENTIFIER_KEY);
            try {
                if (!businessIdentifier) {
                    return;
                }

                const formData = new URLSearchParams();
                formData.append('business_id', businessIdentifier);
                formData.append('email', email);
                formData.append('user_id', user_id.toString());

                await axios.post(`${BASE_URL}/api/sales-person/force-logout`, formData, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                });
                console.log('Sales person forced to log out successfully');
            } catch (error: any) {
                console.error('[authService] Force Logout Error:', error);
            }        
        }
    getToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY);
    }

    getTokenType(): string {
        return localStorage.getItem(this.TOKEN_TYPE_KEY) || 'Bearer';
    }

    getBusinessIdentifier(): string | null {
        return localStorage.getItem(this.BUSINESS_IDENTIFIER_KEY);
    }

    isAuthenticated(): boolean {
        const token = this.getToken();
        // Optionally add token expiration check here if applicable
        const authenticated = !!token;
        return authenticated;
    }
}

// Export an instance of the class
export const authService = new AuthService(); 